import { EmptyState } from '@/components/patterns/empty-state'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { Can } from '@/lib/casl'
import { formatNumber } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { ArrowRight, CalendarOff } from 'lucide-react'
import { ReportCountBadge } from '../-helpers'
import { ClosedDaysCoverageChart } from './-chart'
import { buildClosedDaysChartRows } from './-presentation'
import { getClosedDaysColumns } from './-table-columns'
import type { usePathHelper } from '@/hooks/usePathHelper'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type { ClosedDaysReport } from '@/lib/services/reports/reports.types'

type ClosedDaysReportResultsProps = {
  report?: ClosedDaysReport | null
  orgId: OrganizationIdInput
  path: {
    availability: Pick<ReturnType<typeof usePathHelper>['availability'], 'index'>
  }
}

export function ClosedDaysReportResults({ report, orgId, path }: ClosedDaysReportResultsProps) {
  const locations = report?.locations ?? []
  const selectedDayCount = report?.summary?.selected_day_count ?? 0
  const chartRows = buildClosedDaysChartRows(locations, selectedDayCount)
  const startDate = report?.filters?.start_date
  const calendarLink = startDate
    ? path.availability
        .index()
        .using({ orgId })
        .search({ month: startDate.slice(0, 7) }).link
    : null

  if (!locations.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <EmptyState
            icon={CalendarOff}
            surface="transparent"
            title="No active locations to report"
            description="Active locations will appear here even when their closed-day count is zero."
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="@container/closed-days-report">
      <CardHeader className="gap-4 @2xl/closed-days-report:flex @2xl/closed-days-report:flex-row @2xl/closed-days-report:items-start @2xl/closed-days-report:justify-between">
        <div className="space-y-1.5">
          <CardTitle>Pause coverage by location</CardTitle>
          <CardDescription>
            Each bar covers all {formatNumber(selectedDayCount)} selected{' '}
            {selectedDayCount === 1 ? 'day' : 'days'} without double-counting pickup and delivery
            overlap.
          </CardDescription>
        </div>
        <ReportCountBadge>
          {locations.length} {locations.length === 1 ? 'location' : 'locations'}
        </ReportCountBadge>
      </CardHeader>

      <CardContent className="space-y-8">
        <ClosedDaysCoverageChart rows={chartRows} selectedDayCount={selectedDayCount} />

        <section className="space-y-3" aria-labelledby="closed-days-table-title">
          <div className="space-y-1">
            <Typography id="closed-days-table-title" variant="sectionTitle">
              Closed-day totals
            </Typography>
            <Typography variant="description">
              Unique local dates covered by an explicit pickup pause, delivery pause, or both.
            </Typography>
          </div>
          <IndexDataTable
            mode="client"
            data={locations}
            columns={getClosedDaysColumns()}
            getRowId={(row) => String(row.location_id)}
            pagination={false}
          />
        </section>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border/60 pt-5">
          <Typography variant="caption" className="max-w-3xl">
            Counts include only dates covered by an explicit pickup or delivery pause—even weekends
            and routine closed days. “No explicit pause” does not necessarily mean the location was
            open.
          </Typography>
          {calendarLink ? (
            <Can do="list" on="availability-changes">
              <Typography
                as={Link}
                {...calendarLink}
                variant="caption"
                tone="interaction"
                weight="medium"
                interaction="hoverUnderline"
                className="inline-flex shrink-0 items-center gap-1"
              >
                View Closures &amp; Availability
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Typography>
            </Can>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
